import { Ref, useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  calculateNextIndex,
  calculateNextRowIndex,
  calculatePreviousIndex,
  calculatePreviousRowIndex
} from "shared/lib/RovingTabIndexUtils"
import { ArrowKeys } from "shared/types/ArrowKeys"
import { Direction } from "shared/types/Direction"
import {
  GetRovingTabIndexItemProps,
  OnFocusedItemChange
} from "shared/types/RovingTabIndex"

import { useEvent } from "./useEvent"
import { useFocusWithin } from "./useFocusWithin"
import { useMergedRefs } from "./useMergedRefs"

/**
 * Arguments for the `useRovingTabIndex` hook.
 */
export interface UseRovingTabIndexArgs<D extends Direction> {
  /**
   * Optional. default: `Direction.VERTICAL`. The direction in which the elements are oriented.
   */
  direction?: D
  /**
   * The total number of elements in the roving tab index group.
   */
  elementCount: number
  /**
   * Optional. default: `true`. Determines whether the tab index should wrap around when reaching the first or last element.
   */
  isRoundRobin?: boolean
  /**
   * Optional. default: `false`. If `true` disables roving behavior but retains keyboard conrols.
   */
  disableRovingTabIndex?: boolean
  /**
   * Optional. The index of the first selected element in the roving tab index group. This element will be focused when the group receives focus.
   */
  firstSelectedElementIndex?: number
  /**
   * Optional. Prevent elements from taking focus.
   */
  preventFocus?: boolean
  /**
   * Optional. Function that is called when the focused element changes.
   */
  onFocusedElementChange?: OnFocusedItemChange
}

type UseRovingTabIndexPropsByDirection<D extends Direction> =
  D extends Direction.GRID
    ? UseRovingTabIndexArgs<D> & { columnCount: number }
    : UseRovingTabIndexArgs<D>

export type RovingTabIndexArgs =
  | UseRovingTabIndexPropsByDirection<Direction.VERTICAL>
  | UseRovingTabIndexPropsByDirection<Direction.HORIZONTAL>
  | UseRovingTabIndexPropsByDirection<Direction.GRID>

/**
 * Custom hook for managing roving tab index.
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/Keyboard-navigable_JavaScript_widgets#technique_1_roving_tabindex
 *
 * @template T - The type of the HTML element.
 * @param {RovingTabIndexArgs} args - The args for the hook.
 * @returns {RovingTabIndexResult<T>} - The result object containing the necessary properties and functions. containerProps should be spread on the container element.
 * getElementProps should be called with the index of the element and spread on each element in the list.
 */
export function useRovingTabIndex<
  ElementHTMLElementType extends HTMLElement = HTMLDivElement,
  ContainerHTMLElementType extends HTMLElement = HTMLDivElement
>(args: RovingTabIndexArgs & { ref?: Ref<ElementHTMLElementType> }) {
  const {
    elementCount,
    isRoundRobin = true,
    firstSelectedElementIndex = 0,
    preventFocus = false,
    disableRovingTabIndex = false,
    ref,
    onFocusedElementChange
  } = args
  const [focusedIndex, setFocusedIndex] = useState(0)

  const [internalRef, setInternalRef] = useState<ElementHTMLElementType | null>(
    null
  )
  const [containerRef, setContainerRef] =
    useState<ContainerHTMLElementType | null>(null)
  const requireFocusRef = useRef(true)

  const containsFocus = useFocusWithin(containerRef)

  const columnCount = useMemo(() => {
    if (args.direction === Direction.GRID) {
      return args.columnCount
    }
    if (args.direction === Direction.HORIZONTAL) {
      return args.elementCount
    }
    return 1
  }, [args])

  const rowCount = useMemo(
    () => Math.ceil(elementCount / columnCount),
    [columnCount, elementCount]
  )

  const focusElement = useEvent(() => {
    internalRef?.focus()
    onFocusedElementChange?.({
      index: focusedIndex,
      total: elementCount,
      columnCount,
      rowCount
    })
  })

  useEffect(() => {
    if (focusedIndex !== 0 && elementCount - 1 < focusedIndex) {
      setFocusedIndex(elementCount - 1)
    }
  }, [elementCount])

  useEffect(() => {
    if (
      !requireFocusRef.current ||
      containerRef?.contains(document.activeElement)
    ) {
      focusElement()
    }
  }, [internalRef])

  useEffect(() => {
    if (!containsFocus) {
      setFocusedIndex(firstSelectedElementIndex)
    }
  }, [containsFocus, firstSelectedElementIndex])

  const handleNext = useEvent(() => {
    setFocusedIndex(prev =>
      calculateNextIndex(prev, elementCount, isRoundRobin)
    )
  })
  const handlePrevious = useEvent(() => {
    setFocusedIndex(prev =>
      calculatePreviousIndex(prev, elementCount, isRoundRobin)
    )
  })
  const handleNextRow = useEvent(() => {
    setFocusedIndex(prev =>
      calculateNextRowIndex(prev, elementCount, columnCount, isRoundRobin)
    )
  })
  const handlePreviousRow = useEvent(() => {
    setFocusedIndex(prev =>
      calculatePreviousRowIndex(prev, elementCount, columnCount, isRoundRobin)
    )
  })

  const handleKeydown = useEvent((ev: React.KeyboardEvent) => {
    if (ev.key === ArrowKeys.UP) {
      ev.preventDefault()
      ev.stopPropagation()
      handlePreviousRow()
    }
    if (ev.key === ArrowKeys.RIGHT) {
      ev.preventDefault()
      ev.stopPropagation()
      handleNext()
    }
    if (ev.key === ArrowKeys.DOWN) {
      ev.preventDefault()
      ev.stopPropagation()
      handleNextRow()
    }
    if (ev.key === ArrowKeys.LEFT) {
      ev.preventDefault()
      ev.stopPropagation()
      handlePrevious()
    }
    if (ev.key === "Home") {
      ev.preventDefault()
      ev.stopPropagation()
      setFocusedIndex(0)
    }
    if (ev.key === "End") {
      ev.preventDefault()
      ev.stopPropagation()
      setFocusedIndex(elementCount - 1)
    }
  })

  const mergedRefs = useMergedRefs(setInternalRef, ref)

  const focusedElementProps = useMemo(
    () => ({
      ref: mergedRefs as Ref<ElementHTMLElementType>,
      tabIndex: preventFocus ? -1 : 0
    }),
    [internalRef, ref, preventFocus]
  )
  const unfocusedElementProps = useMemo(() => {
    return { tabIndex: disableRovingTabIndex && !preventFocus ? 0 : -1 }
  }, [disableRovingTabIndex, preventFocus])

  const handleFocus = useEvent((index: number) => {
    setFocusedIndex(index)
    onFocusedElementChange?.({
      index,
      total: elementCount,
      columnCount,
      rowCount
    })
  })

  const getElementProps: GetRovingTabIndexItemProps<ElementHTMLElementType> =
    useCallback(
      (index: number) =>
        index === focusedIndex
          ? focusedElementProps
          : {
              ...unfocusedElementProps,
              onFocus: () => handleFocus(index)
            },
      [focusedIndex, focusedElementProps, unfocusedElementProps]
    )

  const focusOnNextRefChange = useEvent(() => {
    requireFocusRef.current = false
  })

  const containerProps = useMemo<
    Pick<
      React.HTMLProps<ContainerHTMLElementType>,
      "tabIndex" | "onKeyDown"
    > & { ref: Ref<ContainerHTMLElementType> }
  >(
    () => ({
      ref: setContainerRef,
      tabIndex: -1,
      onKeyDown: handleKeydown
    }),
    [containerRef, handleKeydown]
  )

  return {
    ref: internalRef,
    containsFocus,
    focusedIndex,
    containerProps,
    container: containerRef,
    focusElement,
    setFocusedIndex,
    getElementProps,
    focusOnNextRefChange
  }
}
